import { useEffect, useLayoutEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight, X } from "lucide-react";
import { api } from "../lib/api";
import { fetchOnboardingState, clearOnboardingCache } from "./OnboardingGate";
import { useT } from "../lib/i18n";

const isMobile = () => window.matchMedia("(max-width: 1023px)").matches;

const DESKTOP_STEPS = [
  { target: null, title: "Welkom bij Playmaker", body: "In een halve minuut laten we je zien waar alles staat. Je kunt de tour altijd overslaan." },
  { target: "nav-dashboard", title: "Dashboard", body: "Je startpunt: urgente zaken, weekscore, AI-signalen en de checklist om je setup af te maken." },
  { target: "nav-agenda", title: "Agenda", body: "Alle afspraken op één plek. De AI herkent afspraakverzoeken uit je berichten en zet ze klaar ter bevestiging." },
  { target: "nav-inbox", title: "Unified inbox", body: "E-mail en WhatsApp in één overzicht, gekoppeld aan dossiers. Hier beantwoord je alles zonder te schakelen." },
  { target: "nav-clients", title: "Clients (dossiers)", body: "Per speler: contract, commissie, notities, wedstrijden en contactmomenten. Voeg spelers toe of importeer in bulk." },
  { target: "nav-assistant", title: "AI Assistant", body: "Dagelijks een korte lijst met wat aandacht nodig heeft — en hij leert van hoe jij reageert." },
  { target: "nav-matches", title: "Matches", body: "Wedstrijdmeldingen en elke maandag het weekoverzicht met minuten, goals en assists per speler." },
  { target: "nav-briefing", title: "Morning brief", body: "Je ochtendbriefing per e-mail of WhatsApp. Frequentie en kanaal pas je hier aan." },
  { target: "nav-integrations", title: "Instellingen & integraties", body: "E-mail, WhatsApp, team en rechten regel je onder Settings. Klaar — veel succes!" },
];
const MOBILE_STEPS = [
  { target: null, title: "Welkom bij Playmaker", body: "Onderin vind je vijf vaste tabs. We lopen ze kort door." },
  { target: "tab-today", title: "Vandaag", body: "Wat vandaag telt: weekscore, urgente signalen, agenda en AI-suggesties — met directe acties." },
  { target: "tab-agenda", title: "Agenda", body: "Je afspraken, plus AI-voorstellen uit je berichten die je met één tik goedkeurt." },
  { target: "tab-dossiers", title: "Dossiers", body: "Al je spelers met contract, notities en contactmomenten." },
  { target: "tab-programs", title: "Programma's", body: "Je weekscore: contactmomenten, afspraken en taken ten opzichte van je doelen." },
  { target: "tab-messages", title: "Berichten", body: "E-mail en WhatsApp in één inbox. Klaar — veel succes!" },
];

export default function ProductTour() {
  const t = useT();
  const loc = useLocation();
  const [active, setActive] = useState(false);
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const steps = isMobile() ? MOBILE_STEPS : DESKTOP_STEPS;
  const step = steps[i];

  useEffect(() => {
    if (loc.pathname.startsWith("/onboarding")) return;
    fetchOnboardingState().then((s) => { if (s?.show_tour) setActive(true); });
  }, [loc.pathname]);

  useLayoutEffect(() => {
    if (!active || !step?.target) { setRect(null); return; }
    const el = document.querySelector(`[data-testid="${step.target}"]`);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ block: "nearest" });
    const r = el.getBoundingClientRect();
    setRect({ top: r.top - 6, left: r.left - 6, width: r.width + 12, height: r.height + 12 });
  }, [active, i, step]);

  const end = async () => {
    setActive(false);
    await api.post("/onboarding/tour-done").catch(() => {});
    clearOnboardingCache();
  };
  if (!active || !step) return null;

  const mobile = isMobile();
  const cardStyle = rect && !mobile ? { top: Math.min(rect.top, window.innerHeight - 260), left: rect.left + rect.width + 16 } : {};
  return (
    <div className="fixed inset-0 z-[100]" data-testid="product-tour">
      <div className="absolute inset-0 bg-slate-900/60" onClick={end} />
      {rect && <div className="absolute rounded-xl ring-2 ring-white shadow-[0_0_0_9999px_rgba(15,23,42,0.6)] pointer-events-none transition-all duration-300" style={{ ...rect, background: "transparent" }} data-testid="product-tour-spotlight" />}
      <div className={`absolute bg-white rounded-2xl shadow-2xl p-5 w-[min(92vw,360px)] ${!rect || mobile ? "left-1/2 -translate-x-1/2" : ""} ${mobile ? "bottom-24" : !rect ? "top-1/2 -translate-y-1/2" : ""}`} style={cardStyle} data-testid="product-tour-card">
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs font-medium text-slate-400 tabular-nums" data-testid="product-tour-progress">{i + 1} / {steps.length}</span>
          <button onClick={end} className="text-slate-400 hover:text-slate-700" data-testid="product-tour-skip" aria-label={t("Tour overslaan")}><X size={14} /></button>
        </div>
        <div className="text-base font-semibold text-slate-900" data-testid="product-tour-title">{t(step.title)}</div>
        <div className="text-sm text-slate-600 mt-1 leading-relaxed">{t(step.body)}</div>
        <div className="flex items-center gap-2 mt-4">
          <button onClick={() => (i + 1 < steps.length ? setI(i + 1) : end())} className="h-10 px-4 rounded-full text-sm font-semibold text-slate-900 flex items-center gap-1.5" style={{ background: "var(--pm-green)" }} data-testid="product-tour-next">
            {i + 1 < steps.length ? t("Volgende") : t("Aan de slag")} <ArrowRight size={14} />
          </button>
          {i + 1 < steps.length && <button onClick={end} className="h-10 px-3 text-sm text-slate-500" data-testid="product-tour-skip-text">{t("Overslaan")}</button>}
        </div>
      </div>
    </div>
  );
}
